import React, { useState, useEffect, useRef } from 'react';
import styles from './CoachImprovementTracker.module.css';

const CoachImprovementTracker = ({
    facialEngagement,
    handsVisible,
    goodPosture,
    isRecording
}) => {
    const [showImprovementBadge, setShowImprovementBadge] = useState(false);
    const [improvementType, setImprovementType] = useState(null);
    const [improvementLevel, setImprovementLevel] = useState(1); // Track consecutive improvements
    const metricsHistoryRef = useRef({
        facial: [],
        hands: [],
        posture: []
    });

    // Track metrics over time to detect improvements
    useEffect(() => {
        if (!isRecording) {
            return;
        }

        // Record current metrics
        metricsHistoryRef.current.facial.push(facialEngagement);
        metricsHistoryRef.current.hands.push(handsVisible ? 100 : 0);
        metricsHistoryRef.current.posture.push(goodPosture ? 100 : 0);

        // Keep only last 30 readings (approximately 10 seconds at 3fps)
        const maxHistoryLength = 30;
        if (metricsHistoryRef.current.facial.length > maxHistoryLength) {
            metricsHistoryRef.current.facial = metricsHistoryRef.current.facial.slice(-maxHistoryLength);
            metricsHistoryRef.current.hands = metricsHistoryRef.current.hands.slice(-maxHistoryLength);
            metricsHistoryRef.current.posture = metricsHistoryRef.current.posture.slice(-maxHistoryLength);
        }

        // Don't check for improvements until we have enough data
        if (metricsHistoryRef.current.facial.length < 15) return;

        // Calculate improvements by comparing first half average to second half
        const calculateImprovement = (metricName) => {
            const history = metricsHistoryRef.current[metricName];
            const midpoint = Math.floor(history.length / 2);

            const firstHalfAvg = history.slice(0, midpoint).reduce((sum, val) => sum + val, 0) / midpoint;
            const secondHalfAvg = history.slice(-midpoint).reduce((sum, val) => sum + val, 0) / midpoint;

            return {
                metric: metricName,
                improvement: secondHalfAvg - firstHalfAvg,
                firstHalfAvg,
                secondHalfAvg
            };
        };

        // Calculate improvements for all metrics
        const improvements = {
            facial: calculateImprovement('facial'),
            hands: calculateImprovement('hands'),
            posture: calculateImprovement('posture')
        };

        // Find the metric with the most improvement
        let bestImprovement = null;
        let bestImprovementValue = 0;

        Object.values(improvements).forEach(imp => {
            // Only consider significant improvements (>15 points)
            if (imp.improvement > 15 && imp.improvement > bestImprovementValue) {
                bestImprovementValue = imp.improvement;
                bestImprovement = imp;
            }
        });

        // Show improvement badge if we found a significant improvement
        if (bestImprovement && !showImprovementBadge) {
            // If it's the same metric as last time, increment the level
            if (bestImprovement.metric === improvementType) {
                setImprovementLevel(prev => Math.min(prev + 1, 3)); // Max level 3
            } else {
                setImprovementLevel(1);
                setImprovementType(bestImprovement.metric);
            }

            setShowImprovementBadge(true);

            // Hide the badge after 5 seconds
            setTimeout(() => {
                setShowImprovementBadge(false);

                // Reset the history for that metric to avoid showing the same improvement again
                // But keep more recent history for consecutive improvements
                metricsHistoryRef.current[bestImprovement.metric] =
                    metricsHistoryRef.current[bestImprovement.metric].slice(-10);
            }, 5000);
        }
    }, [facialEngagement, handsVisible, goodPosture, isRecording, showImprovementBadge, improvementType]);

    const getImprovementEmoji = () => {
        switch (improvementType) {
            case 'facial':
                return improvementLevel === 1 ? "🙂" : improvementLevel === 2 ? "😃" : "🤩";
            case 'hands':
                return improvementLevel === 1 ? "👋" : improvementLevel === 2 ? "👐" : "🙌";
            case 'posture':
                return improvementLevel === 1 ? "🧍" : improvementLevel === 2 ? "🏋️" : "🏆";
            default:
                return "⭐";
        }
    };

    const getImprovementText = () => {
        const levelTexts = {
            1: {
                facial: "Nice facial expressions!",
                hands: "Better hand gestures!",
                posture: "Posture improving!"
            },
            2: {
                facial: "Great expressiveness!",
                hands: "Excellent gesturing!",
                posture: "Stellar posture!"
            },
            3: {
                facial: "Superstar expressions!",
                hands: "Pro-level gestures!",
                posture: "Perfect posture!"
            }
        };

        return levelTexts[improvementLevel]?.[improvementType] || "You're improving!";
    };

    if (!showImprovementBadge || !isRecording) return null;

    return (
        <div className={`${styles.improvementBadge} ${styles['level' + improvementLevel]}`}>
            <div className={styles.badgeContent}>
                <div className={styles.starBurst}>{getImprovementEmoji()}</div>
                <div className={styles.improvementText}>{getImprovementText()}</div>
            </div>
        </div>
    );
};

export default CoachImprovementTracker;